import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { Dica } from '@/components/Dica.js';

/**
 * A ata da manhã. O Principal vê o rascunho que a IA escreveu a partir da
 * gravação, edita, registra ou descarta as decisões que ela achou na conversa
 * e valida; aí os sócios recebem o resumo. Antes da validação, ninguém mais vê.
 */

type Sugestao = {
  id: string;
  descricao: string;
  responsavel: string | null;
  prazo: string | null;
  motivo: string | null;
};
type Ata = {
  principal: boolean;
  ia_disponivel: boolean;
  estado: 'BUSCANDO_MEET' | 'AGUARDANDO' | 'TRANSCREVENDO' | 'GERANDO_ATA' | 'PRONTA' | 'ERRO' | null;
  erro: string | null;
  validada_em: string | null;
  fatias: number;
  ata: string | null;
  resumo: string | null;
  transcricao: string | null;
  decisoes_sugeridas: Sugestao[];
  /** Manhã pelo Meet: a busca da gravação e da transcrição. */
  meet: { estado: string | null; erro: string | null } | null;
};

/** Como terminou a busca no Meet, quando não trouxe nada. */
const MEET_SEM_NADA: Record<string, string> = {
  SEM_GRAVACAO: 'O Meet não gerou gravação nem transcrição desta manhã.',
  SEM_CONSENTIMENTO:
    'A gravação do Meet não foi trazida: nem todos os presentes consentiram. Os arquivos ficaram no seu Drive.',
  NAO_IMPORTADA: 'Não deu para trazer a gravação do Meet.',
};

/** Trabalhando agora: o texto não se edita até terminar. */
const EM_CURSO = ['TRANSCREVENDO', 'GERANDO_ATA'];
const ESTADO: Record<string, string> = {
  BUSCANDO_MEET:
    'Buscando a gravação e a transcrição no Meet. O Google leva de minutos a algumas horas para gerar os arquivos depois da reunião.',
  AGUARDANDO: 'Na fila: a transcrição começa sozinha em até 15 minutos, ou agora, se você pedir.',
  TRANSCREVENDO: 'Transcrevendo a gravação…',
  GERANDO_ATA: 'Escrevendo o rascunho da ata…',
};
const campo = 'w-full rounded-element border border-ha-border2 bg-transparent px-3 py-2 text-sm';
const botao = 'rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50';

function Secao({ titulo, dica, children }: { titulo: string; dica?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-ha-border2 p-4">
      <h3 className="mb-3 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-ha-muted">
        {titulo}
        {dica ? <Dica texto={dica} /> : null}
      </h3>
      {children}
    </section>
  );
}

export function AtaDaManha({
  manhaId,
  empresaId,
  responsaveis,
}: {
  manhaId: string;
  empresaId: string;
  responsaveis: { membro_id: string; nome: string }[];
}) {
  const qc = useQueryClient();
  const chave = ['conselheiro-ata', manhaId];
  const q = useQuery({
    queryKey: chave,
    queryFn: async () => (await api.get<Ata>(`/conselheiro/manhas/${manhaId}/ata`)).data,
    refetchInterval: (query) => {
      const estado = query.state.data?.estado ?? '';
      if (estado === 'BUSCANDO_MEET') return 30_000;
      return [...EM_CURSO, 'AGUARDANDO'].includes(estado) ? 5000 : false;
    },
  });
  const [ata, setAta] = useState('');
  const [resumo, setResumo] = useState('');
  const [verTranscricao, setVerTranscricao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // O texto do servidor entra no editor quando chega um rascunho novo.
  useEffect(() => {
    if (q.data) {
      setAta(q.data.ata ?? '');
      setResumo(q.data.resumo ?? '');
    }
  }, [q.data?.ata, q.data?.resumo]); // eslint-disable-line react-hooks/exhaustive-deps

  const agir = useMutation({
    mutationFn: async (f: () => Promise<unknown>) => f(),
    onSuccess: () => {
      setErro(null);
      void qc.invalidateQueries({ queryKey: chave });
      void qc.invalidateQueries({ queryKey: ['conselheiro-conducao', manhaId] });
      void qc.invalidateQueries({ queryKey: ['conselheiro-mes'] });
    },
    onError: (e) => setErro(extractError(e).message),
  });

  if (q.isLoading || !q.data) return null;
  const a = q.data;
  const emCurso = EM_CURSO.includes(a.estado ?? '');
  const alterado = ata !== (a.ata ?? '') || resumo !== (a.resumo ?? '');

  // Quem não é o Principal vê a ata só depois de validada.
  if (!a.principal) {
    if (!a.validada_em) return null;
    return (
      <Secao titulo="Ata da manhã">
        {a.resumo ? (
          <>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ha-muted">Resumo</p>
            <p className="mb-4 whitespace-pre-wrap text-sm text-ha-text">{a.resumo}</p>
          </>
        ) : null}
        {a.ata ? <p className="whitespace-pre-wrap text-sm text-ha-subtle">{a.ata}</p> : null}
      </Secao>
    );
  }

  if (a.validada_em) {
    return (
      <Secao titulo="Ata da manhã">
        <p className="mb-3 text-xs text-emerald-600">
          Validada em {new Date(a.validada_em).toLocaleDateString('pt-BR')}. Os sócios receberam o resumo.
        </p>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ha-muted">Resumo</p>
        <p className="mb-4 whitespace-pre-wrap text-sm text-ha-text">{a.resumo}</p>
        <p className="whitespace-pre-wrap text-sm text-ha-subtle">{a.ata}</p>
      </Secao>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Secao
        titulo="Ata da manhã"
        dica="A IA escreve o rascunho a partir da gravação, da pauta e das decisões. Revise, ajuste e valide: só então os sócios veem."
      >
        {emCurso || a.estado === 'AGUARDANDO' || a.estado === 'BUSCANDO_MEET' ? (
          <p className="mb-3 flex items-center gap-2 text-sm text-ha-subtle">
            <span aria-hidden className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
            {ESTADO[a.estado!]}
            {a.fatias ? ` (${a.fatias} ${a.fatias === 1 ? 'fatia' : 'fatias'} de gravação)` : ''}
          </p>
        ) : null}
        {a.estado === 'ERRO' ? <p className="mb-3 text-sm text-red-600">Não deu certo: {a.erro}</p> : null}
        {a.meet && a.estado === 'BUSCANDO_MEET' && a.meet.erro ? (
          <p className="mb-3 text-sm text-red-600">Ainda não consegui ler o Meet: {a.meet.erro}</p>
        ) : null}
        {a.meet && a.estado !== 'BUSCANDO_MEET' && a.meet.estado && MEET_SEM_NADA[a.meet.estado] ? (
          <p className="mb-3 text-sm text-ha-subtle">
            {MEET_SEM_NADA[a.meet.estado]}
            {a.meet.estado === 'NAO_IMPORTADA' && a.meet.erro ? ` ${a.meet.erro}` : ''}
          </p>
        ) : null}
        {a.meet && !emCurso && a.ia_disponivel && (a.meet.estado !== 'BUSCANDO' || a.meet.erro) && a.meet.estado !== 'IMPORTADA' ? (
          <button
            type="button"
            className="mb-3 mr-4 text-sm text-accent underline"
            onClick={() =>
              (!a.ata ||
                window.confirm('Buscar no Meet de novo? Se vier algo, o rascunho da ata e do resumo é refeito.')) &&
              agir.mutate(() => api.post(`/conselheiro/manhas/${manhaId}/meet/buscar`))
            }
          >
            Buscar no Meet de novo
          </button>
        ) : null}
        {!emCurso && a.ia_disponivel && a.estado !== 'BUSCANDO_MEET' ? (
          <button
            type="button"
            className="mb-3 text-sm text-accent underline"
            onClick={() =>
              (!a.ata ||
                window.confirm('Gerar um rascunho novo? O texto atual da ata e do resumo será substituído.')) &&
              agir.mutate(() => api.post(`/conselheiro/manhas/${manhaId}/ata/rascunho`))
            }
          >
            {a.ata
              ? 'Gerar o rascunho de novo'
              : a.estado === 'ERRO'
                ? 'Tentar de novo'
                : a.estado === 'AGUARDANDO'
                  ? 'Gerar agora'
                  : 'Gerar o rascunho com a IA'}
          </button>
        ) : null}
        {!a.ia_disponivel && !a.ata ? (
          <p className="mb-3 text-sm text-ha-muted">A IA não está ligada a este ambiente: escreva a ata abaixo.</p>
        ) : null}

        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ha-muted">
          Resumo para os sócios
        </label>
        <textarea
          className={`${campo} mb-4`}
          rows={5}
          placeholder="Em poucas linhas, o que muda na prática."
          value={resumo}
          onChange={(e) => setResumo(e.target.value)}
          disabled={emCurso}
        />
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ha-muted">Ata</label>
        <textarea
          className={`${campo} font-mono`}
          rows={16}
          placeholder="Presentes, pauta, o que se discutiu e concluiu em cada tema, decisões e próximos passos."
          value={ata}
          onChange={(e) => setAta(e.target.value)}
          disabled={emCurso}
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className={`${botao} border border-ha-border2 !text-ha-text`}
            disabled={!alterado || emCurso}
            onClick={() => agir.mutate(() => api.put(`/conselheiro/manhas/${manhaId}/ata`, { ata, resumo }))}
          >
            Salvar
          </button>
          <button
            type="button"
            className={botao}
            style={{ background: 'var(--primary)' }}
            disabled={emCurso || !ata.trim() || !resumo.trim()}
            onClick={async () => {
              if (!window.confirm('Validar a ata? Os sócios recebem o resumo e a ata não muda mais.')) return;
              if (alterado) await api.put(`/conselheiro/manhas/${manhaId}/ata`, { ata, resumo }).catch(() => undefined);
              agir.mutate(() => api.post(`/conselheiro/manhas/${manhaId}/ata/validar`));
            }}
          >
            Validar a ata
          </button>
          {a.transcricao ? (
            <button
              type="button"
              className="text-sm text-accent underline"
              onClick={() => setVerTranscricao((v) => !v)}
            >
              {verTranscricao ? 'Esconder a transcrição' : 'Ver a transcrição'}
            </button>
          ) : null}
        </div>
        {verTranscricao && a.transcricao ? (
          <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded-card border border-ha-border2 p-3 text-xs text-ha-subtle">
            {a.transcricao}
          </pre>
        ) : null}
        {erro ? <p className="mt-3 text-sm text-red-600">{erro}</p> : null}
      </Secao>

      {a.decisoes_sugeridas.length ? (
        <Secao
          titulo="Decisões que a IA achou na conversa"
          dica="Apareceram na gravação e não foram registradas. Registre as que valem, com responsável e prazo, ou descarte."
        >
          <ul className="flex flex-col gap-3">
            {a.decisoes_sugeridas.map((s) => (
              <SugestaoDeDecisao
                key={s.id}
                s={s}
                responsaveis={responsaveis}
                registrar={(d) =>
                  agir.mutate(async () => {
                    await api.post(`/conselheiro/empresas/${empresaId}/decisoes`, { ...d, manha_id: manhaId });
                    await api.delete(`/conselheiro/manhas/${manhaId}/ata/sugestoes/${s.id}`);
                  })
                }
                descartar={() => agir.mutate(() => api.delete(`/conselheiro/manhas/${manhaId}/ata/sugestoes/${s.id}`))}
              />
            ))}
          </ul>
        </Secao>
      ) : null}
    </div>
  );
}

function SugestaoDeDecisao({
  s,
  responsaveis,
  registrar,
  descartar,
}: {
  s: Sugestao;
  responsaveis: { membro_id: string; nome: string }[];
  registrar: (d: {
    descricao: string;
    responsavel_membro_id: string | null;
    prazo: string | null;
    motivo: string | null;
  }) => void;
  descartar: () => void;
}) {
  // O nome que a IA ouviu vira o responsável, se bater com alguém da empresa.
  const casado = responsaveis.find(
    (r) => s.responsavel && r.nome.toLowerCase().includes(s.responsavel.toLowerCase().split(' ')[0]!),
  );
  const [f, setF] = useState({ descricao: s.descricao, responsavel: casado?.membro_id ?? '', prazo: s.prazo ?? '' });
  return (
    <li className="rounded-card border border-ha-border2 p-3 text-sm">
      <input
        className={`${campo} mb-2`}
        value={f.descricao}
        onChange={(e) => setF({ ...f, descricao: e.target.value })}
      />
      {s.motivo ? <p className="mb-2 text-xs text-ha-muted">{s.motivo}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <select
          className={`${campo} !w-auto`}
          value={f.responsavel}
          onChange={(e) => setF({ ...f, responsavel: e.target.value })}
        >
          <option value="">Responsável</option>
          {responsaveis.map((r) => (
            <option key={r.membro_id} value={r.membro_id}>
              {r.nome}
            </option>
          ))}
        </select>
        <input
          className={`${campo} !w-auto`}
          type="date"
          value={f.prazo}
          onChange={(e) => setF({ ...f, prazo: e.target.value })}
        />
        <button
          type="button"
          className={botao}
          style={{ background: 'var(--primary)' }}
          disabled={f.descricao.trim().length < 5}
          onClick={() =>
            registrar({
              descricao: f.descricao,
              responsavel_membro_id: f.responsavel || null,
              prazo: f.prazo || null,
              motivo: s.motivo,
            })
          }
        >
          Registrar
        </button>
        <button type="button" className="text-xs text-ha-muted underline" onClick={descartar}>
          Descartar
        </button>
      </div>
    </li>
  );
}
